/**
 * Warn when the picked address suggestion is far from the town the customer typed.
 *
 * Google sometimes ranks a far-away match first: "Moshi lodge" returned a guesthouse near
 * Ruaha, ~450 km south, and was quoted as if the customer meant it. This is a hint only —
 * the customer may really mean the far place — so it never blocks the booking.
 */

const EARTH_RADIUS_KM = 6371;

function haversineKm(a, b) {
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(h));
}

/** radiusKm is how far a genuine "<name> …" address can sit from the centre (parks are large). */
export const KNOWN_PLACES = [
  { name: 'Nairobi', pattern: /\bnairobi\b/, lat: -1.2864, lng: 36.8172, radiusKm: 60 },
  { name: 'JKIA', pattern: /\bjkia\b|jomo kenyatta/, lat: -1.3192, lng: 36.9275, radiusKm: 25 },
  { name: 'Arusha', pattern: /\barusha\b/, lat: -3.3869, lng: 36.683, radiusKm: 60 },
  { name: 'Moshi', pattern: /\bmoshi\b/, lat: -3.3488, lng: 37.3388, radiusKm: 60 },
  { name: 'Kilimanjaro Airport', pattern: /\bjro\b|\bkia\b|kilimanjaro (?:international )?airport/, lat: -3.4244, lng: 37.0745, radiusKm: 30 },
  { name: 'Usa River', pattern: /\busa river\b/, lat: -3.3667, lng: 36.85, radiusKm: 30 },
  { name: 'Karatu', pattern: /\bkaratu\b/, lat: -3.3369, lng: 35.6718, radiusKm: 40 },
  { name: 'Mto wa Mbu', pattern: /\bmto wa mbu\b/, lat: -3.3667, lng: 35.85, radiusKm: 30 },
  { name: 'Namanga', pattern: /\bnamanga\b/, lat: -2.5483, lng: 36.7908, radiusKm: 30 },
  { name: 'Nanyuki', pattern: /(?<!ngare )\bnanyuki\b/, lat: 0.0167, lng: 37.0728, radiusKm: 40 },
  { name: 'Naivasha', pattern: /\bnaivasha\b/, lat: -0.7167, lng: 36.4333, radiusKm: 45 },
  { name: 'Nakuru', pattern: /\bnakuru\b/, lat: -0.3031, lng: 36.08, radiusKm: 45 },
  { name: 'Mombasa', pattern: /\bmombasa\b/, lat: -4.0435, lng: 39.6682, radiusKm: 60 },
  { name: 'Diani', pattern: /\bdiani\b/, lat: -4.3167, lng: 39.5833, radiusKm: 30 },
  { name: 'Dar es Salaam', pattern: /\bdar es salaam\b|\bdar\b/, lat: -6.7924, lng: 39.2083, radiusKm: 60 },
  { name: 'Amboseli', pattern: /\bamboseli\b/, lat: -2.65, lng: 37.25, radiusKm: 70 },
  { name: 'Serengeti', pattern: /\bserengeti\b/, lat: -2.3333, lng: 34.8333, radiusKm: 150 },
  { name: 'Ngorongoro', pattern: /\bngorongoro\b/, lat: -3.2, lng: 35.5, radiusKm: 70 },
  { name: 'Tarangire', pattern: /\btarangire\b/, lat: -3.9, lng: 36.0, radiusKm: 80 },
  { name: 'Lake Manyara', pattern: /\bmanyara\b/, lat: -3.5833, lng: 35.8167, radiusKm: 60 },
  { name: 'Lake Natron', pattern: /\bnatron\b/, lat: -2.4167, lng: 36.0333, radiusKm: 60 },
];

/** "Moshi Road", "Arusha–Moshi highway" name a road, not the town the address is in. */
function stripRoadNames(text) {
  return text.replace(
    /\b[a-z]+(?:[\s–-]+[a-z]+)?\s+(?:road|rd|highway|hwy|street|st)\b/g,
    ' '
  );
}

/**
 * @param {string} typedText what the customer typed before picking a suggestion
 * @param {{ label?: string, lat?: number, lng?: number } | null} selected
 * @returns {{ place: string, km: number } | null} the typed place and how far the pick is from it
 */
export function farFromTypedPlace(typedText, selected) {
  if (!typedText || selected?.lat == null || selected?.lng == null) return null;
  const typed = stripRoadNames(typedText.toLowerCase());
  const label = (selected.label || '').toLowerCase();
  const point = { lat: selected.lat, lng: selected.lng };

  const mentioned = KNOWN_PLACES.filter((p) => p.pattern.test(typed));
  if (mentioned.length === 0) return null;

  let nearest = null;
  for (const p of mentioned) {
    // The suggestion itself names the place — trust Google's match.
    if (p.pattern.test(label)) return null;
    const km = haversineKm(point, p);
    if (km <= p.radiusKm) return null;
    if (!nearest || km < nearest.km) nearest = { place: p.name, km };
  }
  return { place: nearest.place, km: Math.round(nearest.km / 10) * 10 };
}

export function typedPlaceWarning(typedText, selected) {
  const far = farFromTypedPlace(typedText, selected);
  if (!far) return null;
  return (
    `This address is about ${far.km} km from ${far.place}. ` +
    `If you meant somewhere in ${far.place}, search again with the lodge or street name.`
  );
}
